import { ZnglyPrivacyUserConsentLevelEnum } from '@/graphql/types';
import { useAppStore } from '@/inertia/AppStore';
import * as CookieConsent from 'vanilla-cookieconsent';
import { getCookie } from '../cookie';

import 'vanilla-cookieconsent/dist/cookieconsent.css';
import './cookieConsent.css';

export const CC_COOKIE_NAME = 'zn/cc_2';

/**
 * Whether the bot mode is enabled or disabled
 * We use this when CI/CD tests are running in order to ensure that the cookie consent banner is visible
 * And we can get the scripts that are blocked by the cookie consent to be executed
 */
export const CC_BOT_MODE = 'zn/cc_bot_mode';
const botMode = getCookie(CC_BOT_MODE);

const privacyConsentLevel: ZnglyPrivacyUserConsentLevelEnum =
  (useAppStore.getState()?.znglyInfo?.privacyUserConsentLevel as ZnglyPrivacyUserConsentLevelEnum) ||
  window?.znRuntime?.privacyUserConsentLevel ||
  ZnglyPrivacyUserConsentLevelEnum.Gdpr;

type ConsentCategory = 'necessary' | 'crm' | 'google';

const updateGoogleConsent = <T extends ConsentCategory[] | string[]>(categories: T) => {
  if (!Array.isArray(categories) || categories?.length === 0) {
    return;
  }

  const consentUpdate: Gtag.ConsentParams = {};

  if (categories.includes('google')) {
    consentUpdate.analytics_storage = 'granted';
  }

  if (categories.includes('crm')) {
    consentUpdate.ad_storage = 'granted';
    consentUpdate.ad_user_data = 'granted';
    consentUpdate.ad_personalization = 'granted';
    consentUpdate.personalization_storage = 'granted';
  }

  if (categories.includes('necessary')) {
    consentUpdate.functionality_storage = 'granted';
  }

  gtag('consent', 'update', consentUpdate);
};

const baseConfig = (): CookieConsent.CookieConsentConfig => ({
  revision: 1,
  mode: 'opt-in',
  autoClearCookies: true,
  disablePageInteraction: true,
  hideFromBots: botMode !== 'disabled',
  cookie: {
    name: CC_COOKIE_NAME,
    path: '/',
  },
  onFirstConsent: ({ cookie }) => {
    updateGoogleConsent(cookie.categories);
  },
  onConsent: ({ cookie }) => {
    updateGoogleConsent(cookie.categories);
  },
  onChange: (param) => {
    const changedCategories = param.changedCategories; // categories that were changed
    const cookieCategories = param.cookie.categories; // current cookie that was just set

    // Update Google Consent Mode with current categories
    updateGoogleConsent(cookieCategories);

    // we want to refresh the page if a cookie was turned off in order to stop the execution of the scripts
    if (
      changedCategories.some((c) => !cookieCategories.includes(c)) // changed categories is not in the final cookie value -> refresh
    ) {
      window.location.reload();
    }
  },
  guiOptions: {
    consentModal: {
      layout: 'box',
      position: 'middle center',
    },
    preferencesModal: {
      layout: 'box',
      flipButtons: true,
    },
  },
  categories: {
    necessary: {
      readOnly: true,
      enabled: true,
    },
    crm: {
      enabled: true,
    },
    google: {
      enabled: true,
      autoClear: {
        cookies: [{ name: '_ga' }],
      },
    },
  },
  language: {
    default: 'en',
    translations: {
      en: {
        consentModal: {
          title: 'We use cookies!',
          acceptAllBtn: 'Accept all',
          showPreferencesBtn: 'Manage preferences',
          description: `
            We use cookies to ensure you get the best experience on our website. 
            You can manage your preferences by clicking on "Manage preferences".
				`,
        },
        preferencesModal: {
          title: 'Manage cookie preferences',
          savePreferencesBtn: 'Save settings',
          acceptAllBtn: 'Accept all',
          sections: [
            {
              title: 'Cookie Usage 📢',
              description: `
									Zngly uses cookies to ensure the basic functionalities of the website and to enhance your online experience.
									You can choose for each category to opt-in/out whenever you want.
								`,
            },
            {
              linkedCategory: 'necessary',
              title: 'Necessary Cookies',
              description: `
									These cookies are necessary for the website to function and cannot be switched off in our systems.
									They are usually only set in response to actions made by you which amount to a request for services,
									such as setting your privacy preferences, logging in or filling in forms.
									You can set your browser to block or alert you about these cookies,
									but some parts of the site will not then work.
									These cookies do not store any personally identifiable information.
								`,
            },
            {
              linkedCategory: 'google',
              title: 'Performance Cookies',
              description: `
									These cookies allow us to count visits and traffic sources so we can measure and improve
									the performance of our site. They help us to know which pages are the most and least popular and
									see how visitors move around the site. All information these cookies collect is aggregated and
									therefore anonymous. If you do not allow these cookies we will not know when you have visited
									our site, and will not be able to monitor its performance.
								`,
            },
            {
              linkedCategory: 'crm',
              title: 'Functional Cookies',
              description: `
									These cookies enable the website to provide enhanced functionality and personalisation. 
									They may be set by us or by third party providers whose services we have added to our pages. 
									If you do not allow these cookies then some or all of these services may not function properly.
								`,
            },
            {
              title: 'More information',
              description: `
									For any queries in relation to our policy on cookies and your choices, please 
									<a class="cc-link" href="mailto:info@zngly.com?subject=Privacy-Policy%20Support">
										Contact Us
									</a>.
								`,
            },
          ],
        },
      },
    },
  },
});

const optOutConfig = (): CookieConsent.CookieConsentConfig => {
  const base = { ...baseConfig() };

  return {
    ...base,
    mode: 'opt-out',
    disablePageInteraction: false,
    autoShow: true,
    guiOptions: {
      consentModal: {
        layout: 'cloud',
        position: 'bottom left',
      },
      preferencesModal: {
        layout: 'box',
        flipButtons: true,
      },
    },
    language: {
      ...base.language,
      translations: {
        en: {
          ...(base.language.translations.en as CookieConsent.Translation),
          consentModal: {
            ...(base.language.translations.en as CookieConsent.Translation).consentModal,
            acceptAllBtn: 'Continue',
          },
        },
      },
    },
  };
};

// Initialize the appropriate consent UI based on privacy level
if (privacyConsentLevel === ZnglyPrivacyUserConsentLevelEnum.OptOut) {
  CookieConsent.run(optOutConfig());
} else if (privacyConsentLevel === ZnglyPrivacyUserConsentLevelEnum.Gdpr) {
  CookieConsent.run(baseConfig());
} else {
  CookieConsent.run(baseConfig());
}
